/**
 * WebSocket Connection Manager
 *
 * Manages WebSocket connection lifecycle, reconnection, and heartbeat
 */

export interface WebSocketMessage {
  type: string;
  timestamp: string;
  data: any;
}

export interface ConnectionEstablishedData {
  connection_id: string;
  user_id: string;
  company_uuids: string[];
  server_time: string;
}

/**
 * Data payload for notification events (info toasts)
 */
export interface NotificationEventData {
  message: string;
  reason?: string;
  server_time?: string;
}

/**
 * Data payload for warning events (dismissable modal/banner)
 */
export interface WarningEventData {
  message: string;
  reason?: string;
  server_time?: string;
}

/**
 * Data payload for logout events (force logout)
 */
export interface LogoutEventData {
  message: string;
  reason?: string;
  server_time?: string;
}

/**
 * Optional due_date (ISO string) for selective page invalidation: list is ordered by due date.
 */
export interface WithDueDate {
  due_date?: string | null;
}

/**
 * Minimal payload for workflow_locked (backend: workflow_run_uuid, company_uuid, report_uuid?, locked_by_actor_uuid, due_date?)
 */
export interface WorkflowLockedEventData extends WithDueDate {
  workflow_run_uuid: string;
  company_uuid: string;
  report_uuid?: string | null;
  locked_by_actor_uuid: string;
  /** Claimant display name (360 Assigned To); optional for older servers. */
  locked_by_name?: string | null;
}

/**
 * Minimal payload for workflow_unlocked (backend: workflow_run_uuid, company_uuid, report_uuid?, unlocked_by_actor_uuid, success, due_date?)
 */
export interface WorkflowUnlockedEventData extends WithDueDate {
  workflow_run_uuid: string;
  company_uuid: string;
  report_uuid?: string | null;
  unlocked_by_actor_uuid: string;
  success: boolean;
}

/**
 * Minimal payload for workflow_lock_failed (backend: workflow_run_uuid, company_uuid, report_uuid?, reason, current_lock_actor_uuid, due_date?)
 */
export interface WorkflowLockFailedEventData extends WithDueDate {
  workflow_run_uuid: string;
  company_uuid: string;
  report_uuid?: string | null;
  reason: string;
  current_lock_actor_uuid: string;
  current_lock_expires_at?: string;
}

/**
 * Minimal payload for workflow_started (backend: workflow_run_uuid, company_uuid, report_uuid?, due_date?)
 */
export interface WorkflowStartedEventData extends WithDueDate {
  workflow_run_uuid: string;
  company_uuid: string;
  report_uuid?: string | null;
  workflow_run_name?: string | null;
}

/**
 * Minimal payload for workflow_status / step change (backend: workflow_run_uuid, company_uuid, report_uuid?, current_step_name?, due_date?)
 */
export interface WorkflowStatusEventData extends WithDueDate {
  workflow_run_uuid: string;
  company_uuid: string;
  report_uuid?: string | null;
  current_step_name?: string;
}

/**
 * Minimal payload for workflow_completed (backend: workflow_run_uuid, company_uuid, report_uuid?, due_date?, name?)
 */
export interface WorkflowCompletedEventData extends WithDueDate {
  workflow_run_uuid: string;
  company_uuid: string;
  report_uuid?: string | null;
  workflow_run_name?: string | null;
}

/**
 * Minimal payload for workflow_cancelled (backend: workflow_run_uuid, company_uuid, report_uuid?, due_date?)
 */
export interface WorkflowCancelledEventData extends WithDueDate {
  workflow_run_uuid: string;
  company_uuid: string;
  report_uuid?: string | null;
  workflow_run_name?: string | null;
}

/**
 * Minimal payload for workflow_failed (backend: workflow_run_uuid, company_uuid, report_uuid?, due_date?)
 */
export interface WorkflowFailedEventData extends WithDueDate {
  workflow_run_uuid: string;
  company_uuid: string;
  report_uuid?: string | null;
}

/**
 * Minimal payload for report_deleted (backend: report_uuid, company_uuid, workflow_run_uuid?)
 */
export interface ReportDeletedEventData {
  report_uuid: string;
  company_uuid: string;
  workflow_run_uuid?: string | null;
}

/**
 * Type map for WebSocket events
 */
export interface WebSocketEventMap {
  connection_established: ConnectionEstablishedData;
  notification: NotificationEventData;
  warning: WarningEventData;
  logout: LogoutEventData;
  workflow_locked: WorkflowLockedEventData;
  workflow_unlocked: WorkflowUnlockedEventData;
  workflow_lock_failed: WorkflowLockFailedEventData;
  workflow_started: WorkflowStartedEventData;
  workflow_status: WorkflowStatusEventData;
  workflow_completed: WorkflowCompletedEventData;
  workflow_cancelled: WorkflowCancelledEventData;
  workflow_failed: WorkflowFailedEventData;
  report_deleted: ReportDeletedEventData;
  [key: string]: any; // Allow any event type for flexibility
}

/** Subscribe with these instead of raw strings so event names stay aligned with WebSocketEventMap. */
export const WORKFLOW_WEBSOCKET_EVENT = {
  LOCKED: "workflow_locked",
  UNLOCKED: "workflow_unlocked",
  LOCK_FAILED: "workflow_lock_failed",
  STATUS: "workflow_status",
  COMPLETED: "workflow_completed",
  CANCELLED: "workflow_cancelled",
  FAILED: "workflow_failed",
  STARTED: "workflow_started",
  REPORT_DELETED: "report_deleted",
} as const;

type ConnectionState = "connecting" | "connected" | "disconnected";

export class WebSocketConnectionManager {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private pingInterval: NodeJS.Timeout | null = null;
  private messageHandlers: Map<string, Set<(data: any) => void>> = new Map();
  private isConnecting = false;
  private isConnected = false;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private tokenRefreshReconnectTimeout: NodeJS.Timeout | null = null;

  // Token refresh state
  private tokenRefreshAttempts = 0;
  private maxTokenRefreshAttempts = 3;
  private tokenRefreshCooldown = 60000; // 1 minute cooldown after max attempts
  private lastTokenRefreshFailure: number | null = null;
  private tokenRefreshBackoffDelay = 2000; // Start with 2 seconds

  constructor(
    private wsUrl: string,
    private getAuthToken: () => Promise<string | null>
  ) {}

  /**
   * Update the auth token callback. Call this when Auth0 context changes
   * to ensure the manager uses the latest token retrieval function.
   */
  updateAuthTokenCallback(newGetAuthToken: () => Promise<string | null>): void {
    this.getAuthToken = newGetAuthToken;
  }

  connect(): void {
    if (this.isConnecting || this.isConnected) {
      return;
    }

    this.isConnecting = true;

    // Get token asynchronously
    this.getAuthToken()
      .then((token) => {
        // Guard: Check if disconnect() was called while waiting for token
        if (!this.isConnecting) {
          return;
        }

        if (!token) {
          console.warn("No auth token available for WebSocket connection");
          this.isConnecting = false;
          return;
        }

        try {
          const url = `${this.wsUrl}?token=${encodeURIComponent(token)}`;
          console.debug(
            "Connecting to WebSocket URL:",
            url.replace(/\?token=.*$/, "?token=...")
          );

          // Validate URL format
          if (
            !this.wsUrl.startsWith("wss://") &&
            !this.wsUrl.startsWith("ws://")
          ) {
            console.error(
              "Invalid WebSocket URL format. Must start with wss:// or ws://"
            );
            this.isConnecting = false;
            return;
          }

          this.ws = new WebSocket(url);

          this.ws.onopen = () => {
            console.debug("WebSocket connected successfully");
            this.isConnecting = false;
            this.isConnected = true;
            this.reconnectAttempts = 0; // Reset on successful connection
            this.reconnectDelay = 1000;

            // Note: tokenRefreshAttempts is NOT reset here. It's only reset
            // after receiving a message, proving the connection is truly stable.
            // This prevents infinite loops if server accepts then immediately
            // closes with 4001.

            // Clear any pending reconnect timeout
            if (this.reconnectTimeout) {
              clearTimeout(this.reconnectTimeout);
              this.reconnectTimeout = null;
            }

            this.startHeartbeat();
          };

          this.ws.onmessage = (event) => {
            this.handleMessage(event.data);
          };

          this.ws.onerror = (error) => {
            console.error("WebSocket error:", error);
            this.isConnecting = false;
          };

          this.ws.onclose = (event) => {
            console.debug("WebSocket closed", {
              code: event.code,
              reason: event.reason || "(no reason provided)",
              wasClean: event.wasClean,
              reconnectAttempts: this.reconnectAttempts,
            });

            this.isConnected = false;
            this.isConnecting = false;
            this.stopHeartbeat();

            // Handle token expiration (4001) with token refresh logic
            if (event.code === 4001) {
              this.handleTokenExpiration();
              return; // Don't use normal reconnection logic
            }

            // Don't reconnect on certain close codes
            const doNotReconnectCodes = [1000, 1008, 4003, 4004];
            if (doNotReconnectCodes.includes(event.code)) {
              console.warn(`Not reconnecting due to close code ${event.code}`);
              return;
            }

            // Attempt reconnection if not a normal closure
            if (
              event.code !== 1000 &&
              this.reconnectAttempts < this.maxReconnectAttempts
            ) {
              this.scheduleReconnect();
            } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
              console.error(
                `Max reconnection attempts (${this.maxReconnectAttempts}) reached. Stopping reconnection.`
              );
            }
          };
        } catch (error) {
          console.error("Failed to create WebSocket connection:", error);
          this.isConnecting = false;
        }
      })
      .catch((error) => {
        console.error("Failed to get auth token for WebSocket:", error);
        this.isConnecting = false;
      });
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.tokenRefreshReconnectTimeout) {
      clearTimeout(this.tokenRefreshReconnectTimeout);
      this.tokenRefreshReconnectTimeout = null;
    }

    this.stopHeartbeat();

    if (this.ws) {
      this.ws.close(1000, "Client disconnect");
      this.ws = null;
    }

    this.isConnected = false;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  private scheduleReconnect(): void {
    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.reconnectAttempts++;

    // First retry is immediate (0ms), subsequent retries use exponential backoff
    const delay =
      this.reconnectAttempts === 1
        ? 0
        : Math.min(
            this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 2),
            30000 // Max 30 seconds
          );

    console.debug(
      `Scheduling WebSocket reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
    );

    this.reconnectTimeout = setTimeout(() => {
      if (
        !this.isConnected &&
        !this.isConnecting &&
        this.reconnectAttempts < this.maxReconnectAttempts
      ) {
        this.connect();
      }
    }, delay);
  }

  private startHeartbeat(): void {
    // Send ping every 9 minutes
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(
          JSON.stringify({
            type: "ping",
            timestamp: new Date().toISOString(),
          })
        );
      }
    }, 540000);
  }

  private stopHeartbeat(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private handleMessage(data: string): void {
    try {
      const message: WebSocketMessage = JSON.parse(data);

      // Reset token refresh state on first successful message.
      // This proves the connection is truly stable (not just opened then
      // immediately closed with 4001).
      if (
        this.tokenRefreshAttempts > 0 ||
        this.lastTokenRefreshFailure !== null
      ) {
        console.debug("Connection stable, resetting token refresh state");
        this.tokenRefreshAttempts = 0;
        this.lastTokenRefreshFailure = null;
      }

      // Handle pong responses (no-op, just confirms connection is alive)
      if (message.type === "pong") {
        return;
      }

      // Route message to registered handlers
      const handlers = this.messageHandlers.get(message.type);
      if (handlers) {
        handlers.forEach((handler) => {
          try {
            handler(message.data);
          } catch (error) {
            console.error(
              `[WebSocket] Handler for "${message.type}" threw error:`,
              error
            );
          }
        });
      }
    } catch (error) {
      console.error("[WebSocket] Failed to parse message:", error);
    }
  }

  // Overloaded on() method for type-safe event handlers
  on<K extends keyof WebSocketEventMap>(
    eventType: K,
    handler: (data: WebSocketEventMap[K]) => void
  ): () => void;
  on(eventType: string, handler: (data: any) => void): () => void;
  on(eventType: string, handler: (data: any) => void): () => void {
    if (!this.messageHandlers.has(eventType)) {
      this.messageHandlers.set(eventType, new Set());
    }
    this.messageHandlers.get(eventType)!.add(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.messageHandlers.get(eventType);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.messageHandlers.delete(eventType);
        }
      }
    };
  }

  getConnectionState(): ConnectionState {
    if (this.isConnecting) return "connecting";
    if (this.isConnected) return "connected";
    return "disconnected";
  }

  private async handleTokenExpiration(): Promise<void> {
    // Check if we're in cooldown period
    if (this.lastTokenRefreshFailure !== null) {
      const timeSinceLastFailure = Date.now() - this.lastTokenRefreshFailure;
      if (timeSinceLastFailure < this.tokenRefreshCooldown) {
        const remainingCooldown = Math.ceil(
          (this.tokenRefreshCooldown - timeSinceLastFailure) / 1000
        );
        console.warn(
          `Token refresh in cooldown. ${remainingCooldown}s remaining.`
        );
        return;
      }
      // Cooldown expired, reset attempts
      this.tokenRefreshAttempts = 0;
      this.lastTokenRefreshFailure = null;
    }

    // Check if we've exceeded max attempts
    if (this.tokenRefreshAttempts >= this.maxTokenRefreshAttempts) {
      console.error(
        `Max token refresh attempts (${this.maxTokenRefreshAttempts}) reached. Entering cooldown period.`
      );
      this.lastTokenRefreshFailure = Date.now();
      this.tokenRefreshAttempts = 0;
      return;
    }

    this.tokenRefreshAttempts++;
    console.debug(
      `Token expired, attempting refresh (attempt ${this.tokenRefreshAttempts}/${this.maxTokenRefreshAttempts})...`
    );

    this.disconnect();

    try {
      // Exponential backoff: 2s, 4s, 8s
      const delay =
        this.tokenRefreshBackoffDelay *
        Math.pow(2, this.tokenRefreshAttempts - 1);
      await new Promise((resolve) => setTimeout(resolve, delay));

      const newToken = await this.getAuthToken();
      if (newToken) {
        // Don't reset tokenRefreshAttempts here. It's only reset in
        // handleMessage() after receiving a message, proving the connection
        // is truly stable. This prevents infinite loops if the server keeps
        // returning 4001 even with fresh tokens.
        console.debug("Token refreshed successfully, reconnecting...");

        // Store timeout so it can be cancelled by disconnect()
        this.tokenRefreshReconnectTimeout = setTimeout(() => {
          this.tokenRefreshReconnectTimeout = null;
          this.connect();
        }, 500);
      } else {
        console.error("Failed to refresh token - retrying with backoff");
        // Retry by calling handleTokenExpiration again (respects max attempts)
        this.handleTokenExpiration();
      }
    } catch (error) {
      console.error("Error during token refresh:", error);
      // Retry by calling handleTokenExpiration again (respects max attempts)
      this.handleTokenExpiration();
    }
  }
}
